import { Entity } from '@api/api/entity-schema';
import {
  ChartSettings,
  checkSearchParams,
  getChartSettingsFromSearchParams,
  ValidChartSettings,
} from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

import { ChartSearchType } from '../../types/search.types';
import { ChartDataResult } from '../use-chart-settings/useChartSettings';
import { ChartState } from '../use-chart-state/useChartState';
import { loadFallbackChart } from './useChartLoad.utils';

export interface UseChartLoadProps {
  initialChartSettings?: ChartSettings;
  initialChartState?: ChartState;
  restoreLastUsedChart?: boolean;
  setHasError: Dispatch<SetStateAction<boolean>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  shouldUseSearchParams?: boolean;
  openSearch?: (
    type: ChartSearchType,
    {
      changingAssets,
      paneIndex,
    }: { changingAssets?: Entity; paneIndex?: number }
  ) => void;
  loadChartFromSettings: (
    settings: ValidChartSettings,
    signal?: AbortSignal
  ) => Promise<ChartDataResult>;
  updateChartState: (partial: Partial<ChartState>) => void;
}

export const useChartLoad = ({
  initialChartSettings,
  initialChartState,
  restoreLastUsedChart,
  setHasError,
  setIsLoading,
  shouldUseSearchParams,
  openSearch,
  loadChartFromSettings,
  updateChartState,
}: UseChartLoadProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const chartLoadAbortController = useRef<AbortController | undefined>(
    undefined
  );

  const handleChartLoadError = () => {
    setHasError(true);

    if (shouldUseSearchParams) {
      setSearchParams('', {
        replace: true,
      });
    }
  };

  const handleInitialChartLoad = async (settings: ValidChartSettings) => {
    chartLoadAbortController.current?.abort();
    const abortController = new AbortController();
    chartLoadAbortController.current = abortController;

    try {
      setIsLoading(true);
      await loadChartFromSettings(settings, abortController.signal);
    } catch {
      if (!abortController.signal.aborted) {
        handleChartLoadError();
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const handleFallbackChartLoad = async (
    shouldLoadFallbackChart = () => true
  ) => {
    chartLoadAbortController.current?.abort();
    const abortController = new AbortController();
    chartLoadAbortController.current = abortController;
    const shouldApplyResult = () =>
      !abortController.signal.aborted && shouldLoadFallbackChart();

    setIsLoading(true);

    try {
      const hasLoaded = await loadFallbackChart(
        settings => loadChartFromSettings(settings, abortController.signal),
        shouldApplyResult
      );

      if (!shouldApplyResult()) {
        return;
      }

      if (!hasLoaded) {
        handleChartLoadError();
      }
    } finally {
      if (shouldApplyResult()) {
        setIsLoading(false);
      }
    }
  };

  const retryInitialChartLoad = () => {
    const settings =
      initialChartSettings ?? getChartSettingsFromSearchParams(searchParams);

    if (checkSearchParams(settings)) {
      setHasError(false);
      handleInitialChartLoad(settings);
    } else if (restoreLastUsedChart) {
      setHasError(false);
      handleFallbackChartLoad();
    }
  };

  useEffect(() => {
    const settings =
      initialChartSettings ?? getChartSettingsFromSearchParams(searchParams);

    if (checkSearchParams(settings)) {
      handleInitialChartLoad(settings);
    } else if (
      initialChartState &&
      !!initialChartState.chartPanes?.[0].chartAssetData.length
    ) {
      setIsLoading(false);
      updateChartState(initialChartState);
    } else if (restoreLastUsedChart) {
      handleFallbackChartLoad();
    } else {
      setIsLoading(false);
      openSearch?.(ChartSearchType.Primary, {});

      if (shouldUseSearchParams) {
        setSearchParams('', {
          replace: true,
        });
      }
    }

    return () => {
      chartLoadAbortController.current?.abort();
    };
  }, [JSON.stringify(initialChartSettings), restoreLastUsedChart]);

  return { handleInitialChartLoad, retryInitialChartLoad };
};
